import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment, a as Host } from './index.js';
import { t as toDateString, i as isValidDateString, d as defineCustomElement$7 } from './p-DzykiKjP.js';
import { d as dateFnsExports } from './p-iWCgRMLC.js';
import { f as forwardEvent } from './p-rUtJRH95.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, b as getError, w as watchError, d as watchWarning, e as getFormFieldAttributes, f as getHostAttributes } from './p-De5ZxJ78.js';
import { g as getBoxAttributes } from './p-CXN6CHBw.js';
import { d as defineCustomElement$a } from './p-BbTg3Car.js';
import { d as defineCustomElement$9 } from './p-D5W-FK48.js';
import { d as defineCustomElement$8 } from './p-D2ldz6Gz.js';
import { d as defineCustomElement$6 } from './p-RWl7wfjL.js';
import { d as defineCustomElement$5 } from './p-BDIehgMZ.js';
import { d as defineCustomElement$4 } from './p-lnuA9A6c.js';
import { d as defineCustomElement$3 } from './p-2MYQ9zL1.js';
import { d as defineCustomElement$2 } from './p-Trmgzgcp.js';
import { d as defineCustomElement$1 } from './p-KbTC_jnR.js';

//#region src/module/format/date-range/index.ts
function isValidValueInDateRange(v) {
	return v === null || typeof v === "string" && isValidDateString(v);
}
function toDateRange(arg) {
	if (typeof arg === "undefined") return void 0;
	if (arg === null) return [null, null];
	if (typeof arg === "string") try {
		const [start$1, end$1] = JSON.parse(arg);
		return [isValidValueInDateRange(start$1) ? start$1 : null, isValidValueInDateRange(end$1) ? end$1 : null];
	} catch {
		return [null, null];
	}
	const [start, end] = arg;
	return [start instanceof Date ? toDateString(start) : start, end instanceof Date ? toDateString(end) : end];
}
function toDateRangeString(dateRange) {
	if (typeof dateRange === "undefined") return void 0;
	if (dateRange === null) return null;
	const [start, end] = dateRange;
	return JSON.stringify([start ? toDateString(start) : start, end ? toDateString(end) : end]);
}
function getDaysInRange(range) {
	const [start, end] = toDateRange(range) ?? [null, null];
	if (start === null || end === null) return 0;
	if (isValidValueInDateRange(start) && isValidValueInDateRange(end)) {
		const days = dateFnsExports.eachDayOfInterval({
			start,
			end
		});
		return days.length;
	}
	return 0;
}
function exceedsMaxDays(range, maxDays) {
	if (typeof maxDays !== "number" || !range) return false;
	if (getDaysInRange(range) > maxDays) return true;
	return false;
}
function isRangeBeforeDate(arg, date) {
	if (!date) return false;
	const [start, end] = toDateRange(arg);
	if (start && dateFnsExports.isBefore(start, date)) return true;
	if (end && dateFnsExports.isBefore(end, date)) return true;
	return false;
}
function isRangeAfterDate(arg, date) {
	if (!date) return false;
	const [start, end] = toDateRange(arg);
	if (start && dateFnsExports.isAfter(start, date)) return true;
	if (end && dateFnsExports.isAfter(end, date)) return true;
	return false;
}
function isRangeInOrder(range) {
	const [start, end] = toDateRange(range) ?? [null, null];
	if (!(start && end)) return true;
	if (start === end) return true;
	if (dateFnsExports.isBefore(end, start)) return false;
	return true;
}

function stringifyValue(dateRange) {
    return toDateRangeString(dateRange);
}
function parseValue(dateRangeString) {
    return toDateRange(dateRangeString);
}
const getNewValue = (idx, newDate, currentValue) => {
    const newValue = parseValue(currentValue) ?? [null, null];
    // Update given idx with new date
    newValue[idx] = newDate || null;
    return newValue;
};
function checkDateBounds({ date, pos, minDate, maxDate, }) {
    // Nothing to check if date is undefined
    if (!date) {
        return;
    }
    // If we can compare to minDate
    if (minDate && dateFnsExports.isBefore(date, minDate)) {
        throw new Error(`The ${pos} date may not be before the min date.`);
    }
    // If we can compare to maxDate
    if (maxDate && dateFnsExports.isAfter(date, maxDate)) {
        throw new Error(`The ${pos} date may not be after the max date.`);
    }
}

const dateRangeInputCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.box{width:100%}.to-label{display:block;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:600;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;line-height:2rem}";

const WuiDateRangeInput = /*@__PURE__*/ proxyCustomElement(class WuiDateRangeInput extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validateInternal = () => {
            // Read-only does not need validation
            if (this.readOnly) {
                return;
            }
            const dateRange = this.value ? parseValue(this.value) : undefined;
            if (this.required && (typeof dateRange === 'undefined' || !(dateRange?.[0] && dateRange[1]))) {
                return {
                    validationMessage: 'Please fill in this field.',
                    validity: getValidityObject({ customError: true, valueMissing: true, valid: false }),
                };
            }
            if (!isRangeInOrder(dateRange)) {
                return {
                    validationMessage: 'The end date may not be before the start date.',
                    validity: getValidityObject({ customError: true, valid: false }),
                };
            }
            try {
                checkDateBounds({ date: dateRange?.[0], pos: 'start', minDate: this.minDate, maxDate: this.maxDate });
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            try {
                checkDateBounds({ date: dateRange?.[1], pos: 'end', minDate: this.minDate, maxDate: this.maxDate });
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            if (exceedsMaxDays(dateRange, this.maxDays)) {
                return {
                    validationMessage: `Date range may not exceed ${this.maxDays} days.`,
                    validity: getValidityObject({ customError: true, valid: false }),
                };
            }
            return {
                validationMessage: '',
                validity: getValidityObject(),
            };
        };
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        this.name = '';
        this.disabled = false;
        /**
         * Disable the component and only use it to display values.
         *
         * This state can be used to hide the calendar, skip validation and disable the inputs.
         */
        this.readOnly = false;
        this.placeholder = 'Select a date range';
        this._handleValueChange = (idx, date) => {
            if (typeof date === 'undefined') {
                return;
            }
            const newValue = getNewValue(idx, date, this.value);
            if (typeof newValue === 'undefined') {
                return;
            }
            this._setValue(newValue);
        };
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    _setValue(value) {
        const newValue = value ? stringifyValue(value) : undefined;
        if (this.value === newValue) {
            // Don't emit same value
            return;
        }
        this.value = newValue;
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        this.dirty = true;
        this.checkValidity();
    }
    renderInner() {
        let valueParsed;
        try {
            valueParsed = parseValue(this.value);
        }
        catch {
            // ignore bad values
        }
        return (h(Fragment, null, h("wui-box", { orientation: "horizontal", gutter: "sm", align: "end", class: "box", ...getBoxAttributes(this) }, h("wui-date-input", { value: valueParsed?.[0] ?? undefined, rangeEndDate: valueParsed?.[1] ?? undefined, onInput: (e) => {
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                const value = target.value;
                this._handleValueChange(0, value);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            }, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, error: this.showValidity ? !!this.validationMessage : false, disabled: this.disabled || this.readOnly, min: (() => {
                // The maxDays prop can only take effect if there is an end date
                const minDateFromMaxDays = valueParsed?.[1] && this.maxDays ? dateFnsExports.subDays(valueParsed[1], this.maxDays - 1) : undefined;
                return toDateString(
                // If we have two min dates, we take the one that's latest
                dateFnsExports.max([this.minDate, minDateFromMaxDays].filter((v) => typeof v !== 'undefined')));
            })(), max: this.maxDate, required: this.required, formField: { label: 'Start date', ...this.dateInputStart?.formField } }), h("b", { class: "to-label" }, "to"), h("wui-date-input", { value: valueParsed?.[1] ?? undefined, rangeStartDate: valueParsed?.[0] ?? undefined, onInput: (e) => {
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                const value = target.value;
                this._handleValueChange(1, value);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            }, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, error: this.showValidity ? !!this.validationMessage : false, disabled: this.disabled || this.readOnly, min: this.minDate, max: (() => {
                // The maxDays prop can only take effect if there is a start date
                const maxDateFromMaxDays = valueParsed?.[0] && this.maxDays ? dateFnsExports.addDays(valueParsed[0], this.maxDays - 1) : undefined;
                return toDateString(
                // If we have two max dates, we take the one that's earliest
                dateFnsExports.min([this.maxDate, maxDateFromMaxDays].filter((v) => typeof v !== 'undefined')));
            })(), required: this.required, formField: { label: 'End date', ...this.dateInputEnd?.formField } }))));
    }
    render() {
        return (h(Host, { key: '33b8f65b751dc0d34538a33da486c8463fff9990', ...getHostAttributes(this) }, h("wui-form-field", { key: '93ca50472b08253ac2a8a6acc870175d99ef2b0c', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get elm() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return dateRangeInputCss; }
}, [257, "wui-date-range-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "name": [1],
        "disabled": [516],
        "readOnly": [516, "read-only"],
        "placeholder": [1],
        "minDate": [1, "min-date"],
        "maxDate": [1, "max-date"],
        "maxDays": [2, "max-days"],
        "dateInputStart": [16, "date-input-start"],
        "dateInputEnd": [16, "date-input-end"],
        "valueAsDateRange": [1040, "value-as-date-range"],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
WuiDateRangeInput.stringifyValue = stringifyValue;
WuiDateRangeInput.parseValue = parseValue;
Object.defineProperties(WuiDateRangeInput.prototype, {
    valueAsDateRange: {
        configurable: true,
        get() {
            return parseValue(this.value);
        },
        set(newValue) {
            this.value = newValue ? stringifyValue(newValue) : undefined;
            this.dirty = true;
            this.checkValidity();
        },
    },
});
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-range-input", "wui-box", "wui-button", "wui-date-input", "wui-date-input-calendar", "wui-form-field", "wui-icon", "wui-loader", "wui-menu-pane", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-range-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateRangeInput);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateRangeInput as W, isRangeAfterDate as a, defineCustomElement as d, exceedsMaxDays as e, isRangeBeforeDate as i, parseValue as p, stringifyValue as s, toDateRange as t };
